<script lang="ts">
  import "@vauban-org/vdl-tokens/tokens.css";
  import "@vauban-org/vdl-tokens/bridge.css";
  // Le vocabulaire de mouvement du design system (courbes de ressort, entrées,
  // cascade `.vdl-stagger-children`). Il existait depuis la v5.0 et n'était
  // importé NULLE PART par le PWA, qui réécrivait donc à la main des animations
  // plus pauvres. L'importer ici, à la racine, le rend disponible partout sans
  // le dupliquer ; il n'ajoute aucune règle tant qu'une classe n'est pas posée.
  //
  // Il porte AUSSI sa propre garde `prefers-reduced-motion` : toute utilité
  // `vdl-animate-*` et la cascade sont coupées sous réglage réduit par le design
  // system lui-même. C'est la raison de préférer ces utilités à des `@keyframes`
  // locaux, que chaque fichier devrait alors penser à couper (et oublierait).
  import "@vauban-org/vdl-tokens/motion.css";
  import "@vauban-org/vdl-tokens/themes/theme-sovereign.css";
  import "@vauban-org/vdl-tokens/themes/theme-obsidian.css";

  import { browser } from "$app/environment";

  let { children } = $props();

  // Apply persisted theme before first paint (runtime sync; FOUC prevented by
  // the inline script in app.html which runs pre-CSS).
  $effect(() => {
    if (!browser) return;
    const saved = localStorage.getItem("vdl-theme-v2") ?? "sovereign";
    document.documentElement.setAttribute("data-theme", saved);
  });

  // VThemeSwitcher will be mounted in marketing landing nav (sprint-776)
</script>

<main>
  {@render children()}
</main>

<style>
  /*
   * La serif éditoriale du journal de mission, AUTO-HÉBERGÉE.
   *
   * Noto Serif, sous SIL Open Font License 1.1 (voir static/fonts/LICENSE.md).
   * Un seul fichier, un seul graisse, sous-ensemble latin + les signes dont le
   * français a besoin : 11 Ko compressés. Souveraineté : l'octet est dans le
   * repo, servi par notre propre nginx ; aucune requête ne part vers un CDN de
   * fontes, ce qui vaut aussi une fuite d'adresse IP en moins par lecteur.
   * (« zéro dépendance » vise npm ; un asset versionné n'en est pas une.)
   *
   * `swap` : le texte se lit dans la fonte système AVANT que la serif arrive,
   * jamais un bloc invisible. `font-synthesis: none` est posé sur les usages
   * (ObserveTranscript) et non ici : cette graisse-là est la seule livrée, et
   * une fausse graisse synthétique sur une serif se voit.
   */
  @font-face {
    font-family: "Noto Serif Vauban";
    src: url("/fonts/noto-serif-latin.woff2") format("woff2");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    /* Déclaré pour que le navigateur ne télécharge rien si la page n'affiche
       aucun caractère couvert. */
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02C6, U+02DA, U+02DC,
      U+0300-0304, U+0308, U+0327, U+0329, U+2000-200B, U+2010-2015, U+2018-201A,
      U+201C-201E, U+2020-2022, U+2026, U+2030, U+2039-203A, U+2044, U+2052,
      U+20AC, U+2122, U+2212;
  }

  /*
   * Le token de fonte serif. Il vit ICI, comme l'alias parchment juste en
   * dessous, parce que `vdl-tokens` n'en fournit aucun (rc.8 n'a que body et
   * mono) ; il passera dans le design system quand celui-ci en aura un.
   *
   * Sur :root, donc hérité par les TROIS thèmes (sovereign, obsidian,
   * parchment). C'est la leçon de #386, où deux thèmes sur trois n'avaient pas
   * les `--vdl-accent-*` : un token posé sur un seul thème est un token cassé
   * deux fois sur trois. Aucun thème ne surcharge celui-ci ; il n'existait pas
   * en amont.
   */
  :global(:root) {
    --vdl-font-serif: "Noto Serif Vauban", Georgia, "Times New Roman", serif;
  }

  /*
   * La typographie DÉTERMINISTE ; ce que la machine du lecteur ne décide plus.
   *
   * LE CONSTAT. `vdl-tokens` déclare `--vdl-font-body: 'Inter', …` et le thème
   * souverain `--vdl-font-display: 'Source Serif 4', …`, mais il n'existe AUCUN
   * `@font-face` pour Inter ni pour Source Serif 4 : ni dans le design system,
   * ni ici, ni via un service de fontes. Ces deux fontes ne s'affichaient donc
   * que sur une machine qui les avait déjà installées, et le rendu changeait
   * d'un lecteur à l'autre sans que personne le voie.
   *
   * PIRE, pour la serif. `--vdl-font-display` n'est déclaré QUE par le thème
   * souverain ; sous obsidienne et parchemin il retombait sur la valeur de
   * `tokens.css`, qui vaut … `'Inter', system-ui, sans-serif`. Les titres
   * éditoriaux (logo, pied de page, sections d'accueil) sortaient donc en SANS
   * sur deux thèmes sur trois, et en Georgia sur le troisième.
   *
   * CE QU'ON FAIT, ET POURQUOI PAS D'AUTO-HÉBERGEMENT D'INTER. Aucun fichier
   * Inter ou Source Serif 4 n'est disponible dans ce dépôt, et on ne télécharge
   * pas une fonte dont on ne peut pas vérifier la licence ici. Plutôt qu'une
   * fonte fantôme, on ASSUME la pile, en trois gestes :
   *
   *   - display : on met en TÊTE la serif qu'on héberge DÉJÀ (Noto Serif
   *     Vauban, juste au-dessus). Zéro octet ajouté, et la voix éditoriale
   *     devient la même sur les trois thèmes et sur toutes les machines. C'est
   *     le gain principal : la serif était le seul caractère de marque, et
   *     c'était justement celui qu'on ne servait pas.
   *   - body : la pile système, écrite explicitement. C'était DÉJÀ ce que la
   *     règle `html, body` ci-dessous servait (`ui-sans-serif, system-ui` en
   *     tête) ; seul le token disait autre chose. On fait coïncider les deux au
   *     lieu de laisser la contradiction, et la règle lit le token désormais.
   *   - data : `--vdl-font-data` n'était déclaré que par le thème souverain ;
   *     aliasé sur `--vdl-font-mono` pour qu'il existe sous les trois.
   *
   * ÉCARTÉ : auto-héberger un sous-ensemble d'Inter comme on l'a fait pour la
   * serif. C'était la voie préférable dans l'absolu, mais elle demandait un
   * fichier qu'on ne peut pas obtenir ni vérifier d'ici ; et elle aurait coûté
   * ~15 à 30 Ko sur un démarrage qu'on cherche par ailleurs à alléger, pour
   * remplacer une pile système qui rend déjà correctement.
   *
   * SPÉCIFICITÉ, et pourquoi ce sélecteur double. `[data-theme="souverain"]`
   * (0,1,0) et `:root` (0,1,0) pèsent PAREIL : seul l'ordre d'émission les
   * départagerait, ce qui dépend du bundler et casserait sans prévenir.
   * `:root[data-theme]` vaut (0,2,0) et tranche donc par construction, quel que
   * soit l'ordre, pour les trois thèmes à la fois. La règle `:root` nue au-dessus
   * reste utile avant que l'attribut soit posé. Un banc le VÉRIFIE sur les trois
   * thèmes plutôt que de faire confiance au raisonnement.
   */
  :global(:root),
  :global(:root[data-theme]) {
    --vdl-font-display: "Noto Serif Vauban", Georgia, "Times New Roman", serif;
    --vdl-font-body:
      ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
      sans-serif;
    --vdl-font-data: var(--vdl-font-mono);
  }

  /* Parchment alias — bridges until vdl-tokens ships theme-parchment.css (post rc.8) */
  :global([data-theme="parchment"]) {
    --vdl-bg-void: var(--vdl-stone-1);
    --vdl-bg-base: var(--vdl-stone-2);
    --vdl-bg-surface: var(--vdl-stone-3);
    --vdl-bg-elevated: var(--vdl-stone-3);
    --vdl-bg-hover: var(--vdl-stone-5);
    --vdl-text-primary: var(--vdl-stone-12);
    --vdl-text-secondary: var(--vdl-stone-11);
    --vdl-text-tertiary: var(--vdl-stone-8);
    --vdl-text-inverse: #FAFAF9;
    --vdl-fg-1: var(--vdl-text-primary);
    --vdl-fg-2: var(--vdl-text-secondary);
    --vdl-fg-3: var(--vdl-text-tertiary);
    --vdl-fg-disabled: var(--vdl-text-tertiary);
    --vdl-border-default: var(--vdl-stone-7);
    --vdl-border-hover: var(--vdl-stone-8);
    --vdl-border-subtle: var(--vdl-stone-6);
    --vdl-border-focus: var(--vdl-indigo-8);
    --vdl-bg-input: var(--vdl-stone-2);
    --vdl-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.06);
    --vdl-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
    --vdl-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.10);
    --vdl-shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.12);
    --vdl-glow-brand: 0 0 20px rgba(99, 102, 241, 0.08);
    --vdl-glow-brand-strong: 0 0 30px rgba(99, 102, 241, 0.14);
    /* Accents sémantiques ; voir le bloc ci-dessous pour pourquoi ils doivent
       être déclarés ici. Sur fond clair, ce sont les pas FONCÉS des échelles
       qui portent le contraste (600/700), jamais les pas moyens. */
    --vdl-accent-primary: var(--vdl-warning-700);
    --vdl-accent-secondary: var(--vdl-stone-9);
    --vdl-accent-functional: var(--vdl-warning-600);
    --vdl-accent-warning: var(--vdl-warning-700);
    --vdl-accent-positive: var(--vdl-success-700);
    --vdl-accent-negative: var(--vdl-error-700);
  }

  /* Complétion sémantique des thèmes non-sovereign.
     SEUL `theme-sovereign.css` déclare `--vdl-accent-{primary,secondary,
     functional,warning,positive,negative}`. Sous obsidian et sous parchment,
     chaque `var(--vdl-accent-*)` de l'application était donc INDÉFINIE : une
     bordure d'alerte retombait sur `currentColor` (invisible) et un texte
     d'alerte héritait simplement de la couleur du texte courant. La cérémonie
     d'approbation perdait ainsi son cadre et son avertissement sur deux des
     trois thèmes, alors que c'est précisément l'écran qui doit se distinguer.
     Déclaré ici par TOKENS de la palette, jamais par hex libre, et au même
     endroit que l'alias parchment que ce fichier portait déjà. */
  :global([data-theme="obsidian"]) {
    --vdl-accent-primary: var(--vdl-gold-9);
    --vdl-accent-secondary: var(--vdl-obsidian-10);
    --vdl-accent-functional: var(--vdl-solar);
    --vdl-accent-warning: var(--vdl-warning-500);
    --vdl-accent-positive: var(--vdl-success-500);
    --vdl-accent-negative: var(--vdl-error-500);
  }

  :global(html, body) {
    margin: 0;
    padding: 0;
    background-color: var(--vdl-bg-base);
    color: var(--vdl-fg-1);
    /* Le token, plus la pile recopiée : une seule source pour la fonte du corps
       de texte (voir le bloc typographie ci-dessus). L'ancienne valeur en dur
       mentionnait « Inter » en repli, une fonte qu'on ne sert pas. */
    font-family: var(--vdl-font-body);
    -webkit-font-smoothing: antialiased;
    min-height: 100dvh;
  }

  main {
    min-height: 100dvh;
  }
</style>
